if (!customElements.get('cart-discount-code')) {
  class CartDiscountCode extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.details = this.querySelector('details');
      this.input = this.querySelector('.ls-cart-coupon__input');
      this.applyButton = this.querySelector('.ls-cart-coupon__apply');
      this.message = this.querySelector('.ls-cart-coupon__message');
      this.cartDrawerRoot = this.closest('cart-drawer');

      this.applyButton?.addEventListener('click', () => this.applyCode());
      this.details?.addEventListener('toggle', () => {
        if (!this.details.open) return;
        requestAnimationFrame(() => this.scrollIntoView({ block: 'nearest' }));
        this.syncAppliedCodes();
      });
      this.input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.applyCode();
      });

      this.bindRemoveButtons();
      if (this.details?.open) this.syncAppliedCodes();
    }

    bindRemoveButtons() {
      this.querySelectorAll('[data-remove-discount-code]').forEach((button) => {
        if (button.dataset.bound === 'true') return;
        button.dataset.bound = 'true';
        button.addEventListener('click', () => this.removeCode(button.dataset.removeDiscountCode));
      });
    }

    endpoint(url) {
      return url.endsWith('.js') ? url : `${url}.js`;
    }

    normalize(code) {
      return code.trim().toLowerCase();
    }

    applicableCodes(cart) {
      return (cart.discount_codes || [])
        .filter((discount) => discount.applicable)
        .map((discount) => discount.code)
        .filter(Boolean);
    }

    uniqueCodes(codes) {
      const seen = new Set();
      return codes.filter((code) => {
        const key = this.normalize(code);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    async getCart() {
      const { response, data: cart } = await window.LSCartRequest.json(this.endpoint(routes.cart_url), {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || window.cartStrings.error);
      return cart;
    }

    async syncAppliedCodes() {
      try {
        const cart = await this.getCart();
        this.renderAppliedCodes(this.applicableCodes(cart));
      } catch (_error) {
        // The coupon form remains usable; submission will surface a specific network error.
      }
    }

    async updateCodes(codes) {
      this.sectionRequests = [{ section: 'cart-drawer' }];
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartItems?.dataset.id) {
        this.sectionRequests.push({
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-contents',
        });
      }
      if (mainCartFooter?.dataset.id) {
        this.sectionRequests.push({
          id: 'main-cart-footer',
          section: mainCartFooter.dataset.id,
          selector: '.js-contents',
        });
      }

      const { response, data: cart } = await window.LSCartRequest.json(
        this.endpoint(routes.cart_update_url),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            discount: this.uniqueCodes(codes).join(','),
            sections: this.sectionRequests.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        }
      );
      if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || window.cartStrings.error);
      return cart;
    }

    setLoading(loading) {
      if (loading) {
        this.cartDrawerRoot?.setAttribute('aria-busy', 'true');
      } else if (!this.cartDrawerRoot?.cartItemsMutationToken) {
        this.cartDrawerRoot?.removeAttribute('aria-busy');
      }
      if (loading) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }
      if (this.input) this.input.disabled = loading;
      if (this.applyButton) {
        this.applyButton.disabled = loading;
        this.applyButton.textContent = loading ? 'Applying…' : 'Apply';
      }
      this.querySelectorAll('[data-remove-discount-code]').forEach((button) => {
        button.disabled = loading;
      });

      const drawer = this.closest('.ls-cart-drawer');
      const cartItems = drawer?.querySelector('#CartDrawer-CartItems');
      const footer = drawer?.querySelector(':scope > .drawer__footer');
      cartItems?.classList.toggle('cart__items--disabled', loading);
      [cartItems, footer].forEach((region) => {
        if (!region) return;
        region.toggleAttribute('inert', loading);
        if (loading) {
          region.setAttribute('aria-busy', 'true');
        } else {
          region.removeAttribute('aria-busy');
        }
      });
    }

    setMessage(text, state = '') {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.dataset.state = state;
    }

    renderAppliedCodes(codes) {
      let container = this.querySelector('.ls-cart-coupon__applied');
      if (!codes.length) {
        container?.remove();
        return;
      }

      if (!container) {
        container = document.createElement('div');
        container.className = 'ls-cart-coupon__applied';
        container.setAttribute('aria-label', 'Applied coupon codes');
        this.querySelector('.ls-cart-coupon__panel')?.append(container);
      }

      container.replaceChildren();
      codes.forEach((code) => {
        const chip = document.createElement('span');
        chip.className = 'ls-cart-coupon__code';

        const label = document.createElement('span');
        label.textContent = code;

        const removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.dataset.removeDiscountCode = code;
        removeButton.setAttribute('aria-label', `Remove coupon ${code}`);
        removeButton.textContent = '×';

        chip.append(label, removeButton);
        container.append(chip);
      });
      this.bindRemoveButtons();
    }

    async refreshDrawer(cart, { inputValue = '', message = '', state = '', focusInput = false } = {}) {
      const sectionHtml = cart.sections?.['cart-drawer'];
      const cartDrawer = document.querySelector('cart-drawer');
      const targetInner = cartDrawer?.querySelector('.drawer__inner');

      let drawerReplaced = false;
      if (sectionHtml && targetInner) {
        const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const sourceDrawer = html.querySelector('cart-drawer');
        const sourceInner = sourceDrawer?.querySelector('.drawer__inner');
        if (sourceInner) {
          cartDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
          targetInner.replaceWith(sourceInner);
          cartDrawer.syncBusyState?.();
          drawerReplaced = true;
        }
      }

      if (!drawerReplaced) {
        await document.querySelector('cart-drawer-items')?.onCartUpdate();
      }

      (this.sectionRequests || []).forEach((section) => {
        if (!section.id || !section.selector) return;
        const sectionHtml = cart.sections?.[section.section];
        const targetSection = document.getElementById(section.id);
        if (!sectionHtml || !targetSection) return;

        const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const sourceContent = html.querySelector(section.selector);
        const targetContent = targetSection.querySelector(section.selector) || targetSection;
        if (sourceContent && targetContent) targetContent.innerHTML = sourceContent.innerHTML;
      });

      const nextCoupon = cartDrawer?.querySelector('cart-discount-code');
      if (!nextCoupon) return;

      nextCoupon.details.open = true;
      nextCoupon.input.value = inputValue;
      nextCoupon.renderAppliedCodes(nextCoupon.applicableCodes(cart));
      nextCoupon.setMessage(message, state);

      requestAnimationFrame(() => {
        if (!cartDrawer.classList.contains('active')) return;
        const focusContainer = cartDrawer.querySelector('#CartDrawer');
        const focusTarget = focusInput ? nextCoupon.input : nextCoupon.querySelector('summary');
        if (focusContainer && focusTarget && typeof trapFocus === 'function') {
          trapFocus(focusContainer, focusTarget);
        } else {
          focusTarget?.focus();
        }
      });
    }

    async applyCode() {
      if (
        this.cartDrawerRoot?.getAttribute('aria-busy') === 'true' ||
        this.cartDrawerRoot?.cartItemsMutationToken
      ) return;
      const code = this.input?.value.trim();
      if (!code) {
        this.setMessage('Enter a coupon code.', 'error');
        this.input?.focus();
        return;
      }

      this.setLoading(true);
      this.setMessage('');

      try {
        const currentCart = await this.getCart();
        const existingCodes = this.applicableCodes(currentCart);
        if (existingCodes.some((existingCode) => this.normalize(existingCode) === this.normalize(code))) {
          this.setMessage('This coupon is already applied.', 'success');
          this.input.select();
          return;
        }

        const updatedCart = await this.updateCodes([...existingCodes, code]);
        const codeResult = (updatedCart.discount_codes || []).find(
          (discount) => this.normalize(discount.code) === this.normalize(code)
        );

        if (!codeResult?.applicable) {
          const rollbackCart = await this.updateCodes(existingCodes);
          await this.refreshDrawer(rollbackCart, {
            inputValue: code,
            message: "This code isn't valid for this cart or can't be combined with the current bundle.",
            state: 'error',
            focusInput: true,
          });
          return;
        }

        await this.refreshDrawer(updatedCart, {
          message: `Coupon ${codeResult.code} applied.`,
          state: 'success',
        });
      } catch (_error) {
        this.setMessage('The coupon could not be applied. Try again.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    async removeCode(code) {
      if (
        this.cartDrawerRoot?.getAttribute('aria-busy') === 'true' ||
        this.cartDrawerRoot?.cartItemsMutationToken
      ) return;
      if (!code) return;
      this.setLoading(true);
      this.setMessage('');

      try {
        const currentCart = await this.getCart();
        const remainingCodes = this.applicableCodes(currentCart).filter(
          (existingCode) => this.normalize(existingCode) !== this.normalize(code)
        );
        const updatedCart = await this.updateCodes(remainingCodes);
        await this.refreshDrawer(updatedCart, {
          message: `Coupon ${code} removed.`,
          state: 'success',
        });
      } catch (_error) {
        this.setMessage('The coupon could not be removed. Try again.', 'error');
      } finally {
        this.setLoading(false);
      }
    }
  }

  customElements.define('cart-discount-code', CartDiscountCode);
}
